import PropTypes from 'prop-types'
import Styles from './ChargePointTabs.module.css'
import ChargePointSelectors from 'Stores/ChargePoint/Selectors'
import ChargePointActions from 'Stores/ChargePoint/Actions'
import { Colors } from 'Theme'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import React, { useEffect, useRef } from 'react'
import { ChargePointTab, ChargePointTabSkeleton, IFSkeleton } from 'Components'
import AddIcon from '@mui/icons-material/Add'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import Tooltip from '@mui/material/Tooltip'
import Icon from '@mui/material/Icon'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import InfinityEnums from 'Enums/InfinityEnums'
import SiteTransactionsModal from 'Containers/SiteTransactionsModal/SiteTransactionsModal'
import RequestState from 'Enums/RequestState'
import styled from 'styled-components'
import StationSelectors from 'Stores/Station/Selectors'
import history from 'history/browser'
import AuthSelectors from 'Stores/Auth/Selectors'
import TabScrollButton from '@mui/material/TabScrollButton'
import { Emitter } from '../../Services/EmitterService'
import { styled as muiStyled } from '@mui/system'

const Container = styled.div`
  background-color: ${(props) => props.TabsBackground};
`

const AddContainer = styled.div`
  color: ${(props) => props.black};
`

const MyTabScrollButton = muiStyled(TabScrollButton)(({ theme }) => ({
  width: 30,
  overflow: 'hidden',
  transition: 'width 0.3s',
  '&.Mui-disabled': {
    width: 0,
  },
}))

const ChargePointTabs = ({
  stationList,
  fetchStationListRequestState,
  fetchChargePointList,
  fetchChargePoint,
  chargePointList,
  connectors,
  fetchChargePointListRequestState,
  stationPaginationOffset,
  selectedIndex,
  stationSelectedIndex,
  setSelectedIndex,
  onAddClick,
  stationsFilter,
  addPermission,
  emitterUpdateChargePoint,
  clearChargePoints,
  resetChargePointState,
  emitterAddChargePoint,
  emitterDeleteChargePoint,
  deleteMovedChargePointRequestState,
  deleteChargePointRequestState,
  emitterUpdateChargePointDiagnostics,
  resetChargePointSpecifications,
  operator,
  operatorUser,
}) => {
  const shouldUpdateChargePoints = useRef()
  const siteTransactionsModalRef = useRef()
  const { t } = useTranslation()

  const isHeaderLoading =
    fetchChargePointListRequestState === RequestState.LOADING ||
    fetchStationListRequestState === RequestState.LOADING
  const isSiteSelected = Boolean(stationList[stationSelectedIndex])
  const canDownloadTransactions = operatorUser?.permissions?.includes(
    InfinityEnums.OperatorUserPermission.CAN_DOWNLOAD_TRANSACTIONS,
  )

  useEffect(() => {
    if (deleteMovedChargePointRequestState === RequestState.SUCCEEDED) {
      if (chargePointList.length > 0) {
        const slashArray = history.location.pathname.split('/')
        const stationId = slashArray[slashArray.length - 1]

        if (stationId !== chargePointList[selectedIndex]._id) {
          let currentPath = history.location.pathname
          const index = currentPath.indexOf('station')
          if (index !== -1) {
            currentPath = currentPath.substring(0, index - 1)
            currentPath =
              currentPath + `/stations/${chargePointList[selectedIndex]._id}`
            history.push({ pathname: currentPath })
          }
        }
      } else {
        let currentPath = history.location.pathname
        const index = currentPath.indexOf('station')
        if (index !== -1) {
          currentPath = currentPath.substring(0, index - 1)
          history.push({ pathname: currentPath })
        }
      }
      if (deleteChargePointRequestState !== RequestState.LOADING)
        resetChargePointState()
    }
  }, [deleteMovedChargePointRequestState])

  useEffect(() => {
    chargePointList.forEach((chargePoint) => {
      Emitter.subscribeToChannel(
        `operator/${operator._id}/chargePoint/${chargePoint.huid}/updateConnector/`,
        operator.channelKey,
        emitterUpdateChargePoint,
        {},
      )
      Emitter.subscribeToChannel(
        `operator/${operator._id}/chargePoint/${chargePoint.huid}/editChargePoint/`,
        operator.channelKey,
        emitterUpdateChargePoint,
        {},
      )
      Emitter.subscribeToChannel(
        `operator/${operator._id}/chargePoint/${chargePoint.huid}/chargePointOnline/`,
        operator.channelKey,
        emitterUpdateChargePoint,
        {},
      )
      Emitter.subscribeToChannel(
        `operator/${operator._id}/chargePoint/${chargePoint.huid}/updateDiagnostics/`,
        operator.channelKey,
        emitterUpdateChargePointDiagnostics,
        {},
      )
    })
  }, [chargePointList, operator])

  useEffect(() => {
    shouldUpdateChargePoints.current = false
    return () => {
      Emitter.unsubscribeFromChannelGroup(
        `operator/${operator._id}/chargePoint/`,
        operator.channelKey,
      )
    }
  }, [])

  useEffect(() => {
    if (
      (stationList[stationSelectedIndex] && stationPaginationOffset === 1) ||
      (stationList[stationSelectedIndex] && shouldUpdateChargePoints.current)
    ) {
      if (shouldUpdateChargePoints.current) {
        shouldUpdateChargePoints.current = false
      }
      fetchChargePointList(stationList[stationSelectedIndex]._id)
    }
    if (stationList[stationSelectedIndex]?._id) {
      const stationId = stationList[stationSelectedIndex]._id
      Emitter.subscribeToChannel(
        `operator/${operator._id}/station/${stationId}/chargePointActions/addChargePoint/`,
        operator.channelKey,
        emitterAddChargePoint,
        {},
      )
      Emitter.subscribeToChannel(
        `operator/${operator._id}/station/${stationId}/chargePointActions/deleteChargePoint/`,
        operator.channelKey,
        emitterDeleteChargePoint,
        {},
      )
      Emitter.subscribeToChannel(
        `operator/${operator._id}/station/${stationId}/chargePointActions/moveChargePoint/`,
        operator.channelKey,
        (response) => {
          if (
            stationList[stationSelectedIndex]._id === response.data.oldStation
          )
            emitterDeleteChargePoint({ data: response.data.chargePoint.huid })

          if (
            stationList[stationSelectedIndex]._id === response.data.newStation
          )
            emitterAddChargePoint({ data: response.data.chargePoint })
        },
        {},
      )
    }

    return () => {
      if (stationList[stationSelectedIndex] && stationPaginationOffset === 0)
        clearChargePoints()
    }
  }, [stationSelectedIndex])

  useEffect(() => {
    shouldUpdateChargePoints.current = true
  }, [stationsFilter])

  useEffect(() => {
    if (chargePointList[selectedIndex]) {
      let currentPath = history.location.pathname
      const index = currentPath.indexOf('station')
      if (index !== -1) {
        currentPath = currentPath.substring(0, index - 1)
        const chargePointId = chargePointList[selectedIndex]['_id']
        currentPath = currentPath + `/stations/${chargePointId}`
        history.push({ pathname: currentPath })
      } else {
        const siteExists = history.location.pathname.indexOf('site')
        const slashArray = history.location.pathname.split('/')

        if (siteExists !== -1 && slashArray.length !== 2) {
          currentPath = history.location.pathname
          const chargePointId = chargePointList[selectedIndex]['_id']
          currentPath = currentPath + `/stations/${chargePointId}`
          history.push({ pathname: currentPath })
        }
      }
    }
  }, [selectedIndex])

  const handleChange = (event, newValue) => {
    setSelectedIndex(newValue)
    resetChargePointState()
    resetChargePointSpecifications()
    fetchChargePoint(chargePointList[newValue]._id)
  }

  const chargePointTabsContainerRef = useRef()
  const getChargePointTabsSkeletonsCount = () => {
    if (chargePointTabsContainerRef.current) {
      let containerHeight =
        chargePointTabsContainerRef.current.parentElement.clientWidth / 16
      return Math.floor(containerHeight / 10)
    }
    return 9
  }

  return (
    <Container
      TabsBackground={Colors.TabsBackground}
      className={Styles.Container}
    >
      <Tabs
        ScrollButtonComponent={MyTabScrollButton}
        scrollButtons="auto"
        value={selectedIndex ? selectedIndex : 0}
        onChange={handleChange}
        {...(chargePointList.length !== 0 ? { variant: 'scrollable' } : {})}
        TabIndicatorProps={{
          style: {
            backgroundColor:
              fetchChargePointListRequestState === RequestState.LOADING ||
              fetchStationListRequestState === RequestState.LOADING
                ? Colors.transparent
                : Colors.primary,
          },
        }}
        ref={chargePointTabsContainerRef}
      >
        {fetchChargePointListRequestState === RequestState.LOADING ||
        fetchStationListRequestState === RequestState.LOADING
          ? [...Array(getChargePointTabsSkeletonsCount())].map((e, index) => (
              <Tab
                sx={{ padding: '0px' }}
                key={index}
                label={<ChargePointTabSkeleton />}
                disabled
              />
            ))
          : chargePointList?.map((chargePoint, index) => (
              <Tab
                sx={{ padding: '0px' }}
                key={index}
                label={
                  <ChargePointTab
                    name={chargePoint.name}
                    connectors={connectors[index]}
                    isSelected={selectedIndex === index}
                    isActivated={chargePoint.isActivated}
                    isConnecting={
                      !chargePoint.isActivated &&
                      (!chargePoint.evses || chargePoint.evses.length === 0
                        ? 0
                        : chargePoint.evses.flatMap(
                            (evse) => evse.connectors || [],
                          ).length) === 0
                    }
                  />
                }
              />
            ))}
      </Tabs>
      <div className={Styles.RightActions}>
        {canDownloadTransactions && isSiteSelected ? (
          isHeaderLoading ? (
            <IFSkeleton variant="circular" width="1.5rem" height="1.5rem" />
          ) : (
            <Tooltip title={t('SiteTransactionsModal.Tooltip')}>
              <div
                className={Styles.DownloadContainer}
                onClick={() => siteTransactionsModalRef.current?.show()}
              >
                <Icon fontSize="medium" component={FileDownloadOutlinedIcon} />
              </div>
            </Tooltip>
          )
        ) : null}
        <AddContainer black={Colors.black} className={Styles.AddContainer}>
          {addPermission ? (
            isHeaderLoading ? (
              <IFSkeleton variant="circular" width="1.5rem" height="1.5rem" />
            ) : (
              <Icon
                fontSize="medium"
                component={AddIcon}
                onClick={
                  fetchChargePointListRequestState === RequestState.LOADING
                    ? () => {}
                    : onAddClick
                }
              />
            )
          ) : null}
        </AddContainer>
      </div>
      <SiteTransactionsModal ref={siteTransactionsModalRef} />
    </Container>
  )
}

function mapDispatchToProps(dispatch) {
  return {
    setSelectedIndex: (index) =>
      dispatch(ChargePointActions.setChargePointSelectedIndex(index)),
    fetchChargePointList: (stationId) =>
      dispatch(ChargePointActions.fetchChargePointList(stationId)),
    fetchChargePoint: (chargePointId) =>
      dispatch(ChargePointActions.fetchChargePoint(chargePointId)),
    emitterUpdateChargePoint: (args) =>
      dispatch(ChargePointActions.emitterUpdateChargePoint(args)),
    clearChargePoints: () => dispatch(ChargePointActions.clearChargePoints()),
    resetChargePointState: () =>
      dispatch(ChargePointActions.resetChargePointState()),
    emitterAddChargePoint: (newChargePoint) =>
      dispatch(ChargePointActions.emitterAddChargePoint(newChargePoint)),
    emitterDeleteChargePoint: (chargePointUid) =>
      dispatch(ChargePointActions.emitterDeleteChargePoint(chargePointUid)),
    emitterUpdateChargePointDiagnostics: (status) =>
      dispatch(ChargePointActions.emitterUpdateChargePointDiagnostics(status)),
    resetChargePointSpecifications: () =>
      dispatch(ChargePointActions.resetChargePointSpecifications()),
  }
}

const mapStateToProps = (state) => ({
  chargePointList: ChargePointSelectors.getChargePoints(state),
  connectors: ChargePointSelectors.getFormattedChargePointConnectors(state),
  fetchChargePointListRequestState:
    ChargePointSelectors.getFetchChargePointListRequestState(state),
  selectedIndex: ChargePointSelectors.getSelectedIndex(state),
  stationList: StationSelectors.getStationsList(state),
  fetchStationListRequestState:
    StationSelectors.getFetchStationListRequestState(state),
  stationSelectedIndex: StationSelectors.getSelectedIndex(state),
  stationPaginationOffset: StationSelectors.getPaginationOffset(state),
  stationsFilter: StationSelectors.getStationsFilter(state),
  deleteMovedChargePointRequestState:
    ChargePointSelectors.getDeleteMovedChargePointRequestState(state),
  deleteChargePointRequestState:
    ChargePointSelectors.getDeleteChargePointRequestState(state),
  operator: AuthSelectors.getOperator(state),
  operatorUser: AuthSelectors.getOperatorUser(state),
})

ChargePointTabs.propTypes = {
  chargePointList: PropTypes.arrayOf(PropTypes.object),
  fetchChargePointListRequestState: PropTypes.number,
  selectedIndex: PropTypes.number,
  onAddClick: PropTypes.func,
  addPermission: PropTypes.bool,
  operatorUser: PropTypes.object,
}

export default connect(mapStateToProps, mapDispatchToProps)(ChargePointTabs)
